import {
  AnalyticsEventLocation,
  getAnalyticsItemCategoriesByTaxonomy,
  getAnalyticsItemType,
} from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { useSeeAllData } from '../hooks/useSeeAllData';
import { ProductSearchMaterial } from '../types';

interface IAnalyticsEvent {
  event: EventName;
  type?: EventType | null;
  action?: EventAction | CarouselAction | null;
  name?: string | null;
  method?: string | null;
  experiment_name?: string | null;
  experiment_version?: string | null;
  event_location?: AnalyticsEventLocation | null;
  list_id?: string | null;
  list_name?: string | null;
  item_list_id?: string;
  item_list_name?: string;
  status?: string | null;
  filter?: IFilterEvent | null;
  content_id?: string | null;
  content_type?: string | null;
  ecommerce?: any | null;
  design_type?: string | null;
}

export enum EventName {
  FILTER = 'filter',
  CHANGE_VIEW = 'change_view',
  SELECT_CONTENT = 'select_content',
  VIEW_PROMOTION = 'view_promotion',
  SELECT_PROMOTION = 'select_promotion',
  CAROUSEL = 'carousel',
  DESIGN_WAVE_EXPERIMENT_SHOWN = 'design_wave_experiment_shown',
  A1_BLOCK_EXPERIMENT_SHOWN = 'srp_a1_recos_experiment_shown',
  A1_BLOCK_SHOWN_LIST = 'view_item_list',
}

interface IFilterEvent {
  action: FilterAction;
  type: FilterType;
  attribute: string | null;
  name: string;
  results: number;
}

enum FilterAction {
  Apply = 'Apply',
  Remove = 'Remove',
}

enum FilterType {
  Category = 'Category',
}

export enum EventType {
  Layout = 'Layout',
}

export enum EventAction {
  DefaultView = 'Default view',
  ListView = 'List view',
}

// need to wait when window.dataLayer will be initialized and have gtm.load event
const checkDataLayer = (): Promise<void> => {
  return new Promise((resolve) => {
    const check = () => {
      if (
        window.dataLayer &&
        window.dataLayer.find((event: any) => event.event === 'gtm.load')
      ) {
        resolve();
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
};

export const sendAnalyticsEvent = async (params: IAnalyticsEvent) => {
  await checkDataLayer();
  // console.log(params);
  window.dataLayer.push(params);
};

export const clearAnalyticsEvents = async (params: any) => {
  await checkDataLayer();
  window.dataLayer.push(params);
};

export const sendSeeAllAnalyticsEvent = (
  title: string,
  eventLocation: AnalyticsEventLocation | null,
) => {
  const { contentId, contentType } = useSeeAllData(title);

  sendAnalyticsEvent({
    event: EventName.SELECT_CONTENT,
    content_id: contentId,
    content_type: contentType,
    name: `${title}`,
    experiment_name: null,
    experiment_version: null,
    event_location: eventLocation ? eventLocation : null,
    method: null,
    action: null,
    list_id: null,
    list_name: null,
    type: null,
  });
};

interface CategoryEvent {
  categoryId: string | null;
  selectedCategoryId: string | null;
  name: string;
  results: number;
}

export const sendCategoryChangeAnalyticsEvent = (
  params: CategoryEvent,
  eventLocation: AnalyticsEventLocation | null,
) => {
  const { categoryId, selectedCategoryId, name, results } = params;
  const filterAction =
    categoryId === selectedCategoryId
      ? FilterAction.Remove
      : FilterAction.Apply;
  clearAnalyticsEvents({
    filter: null,
  }).then(() => {
    sendAnalyticsEvent({
      event: EventName.FILTER,
      filter: {
        action: filterAction,
        type: FilterType.Category,
        attribute: null,
        name: name,
        results: results,
      },
      type: null,
      action: null,
      name: null,
      method: null,
      experiment_name: null,
      experiment_version: null,
      event_location: eventLocation ? eventLocation : null,
      list_id: null,
      list_name: null,
      status: null,
    });
  });
};

export const sendChangeViewAnalyticsEvent = (
  isListView: boolean,
  eventLocation: AnalyticsEventLocation | null,
) => {
  sendAnalyticsEvent({
    event: EventName.CHANGE_VIEW,
    type: EventType.Layout,
    action: isListView ? EventAction.ListView : EventAction.DefaultView,
    name: null,
    method: null,
    experiment_name: null,
    experiment_version: null,
    event_location: eventLocation ? eventLocation : null,
    list_id: null,
    list_name: null,
    status: null,
  });
};

export interface CollectionItemAnalyticsData {
  name: string;
  index: number;
}

interface ViewPromotionAnalyticsEvent {
  collectionName: string;
  items: CollectionItemAnalyticsData[];
  eventLocation: AnalyticsEventLocation | null;
}

export const sendViewPromotionAnalyticsEvent = ({
  collectionName,
  items,
  eventLocation,
}: ViewPromotionAnalyticsEvent) => {
  const promotionId = `${collectionName?.split(' ').join('_').toLowerCase()}`;
  const promotionName = `${collectionName}`;

  sendAnalyticsEvent({
    event: EventName.VIEW_PROMOTION,
    event_location: eventLocation ? eventLocation : null,
    list_id: promotionId,
    list_name: promotionName,
    experiment_name: null,
    experiment_version: null,
    method: null,
    action: null,
    type: null,
    name: null,
    status: null,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: items.map((item) => ({
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: item.name,
        creative_slot: `${promotionId}_${item.index + 1}`,
      })),
    },
  });
};

export const sendSelectPromotionAnalyticsEvent = (
  collectionName: string,
  item: CollectionItemAnalyticsData,
  eventLocation: AnalyticsEventLocation | null,
) => {
  const promotionId = `${collectionName.split(' ').join('_').toLowerCase()}`;
  const promotionName = `${collectionName}`;

  sendAnalyticsEvent({
    event: EventName.SELECT_PROMOTION,
    event_location: eventLocation ? eventLocation : null,
    list_id: promotionId,
    list_name: promotionName,
    experiment_name: null,
    experiment_version: null,
    method: null,
    action: null,
    type: null,
    name: null,
    status: null,
    ecommerce: {
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: [
        {
          promotion_id: promotionId,
          promotion_name: promotionName,
          creative_name: item.name,
          creative_slot: `${promotionId}_${item.index + 1}`,
        },
      ],
    },
  });
};

export enum CarouselAction {
  FORWARD = 'Forward',
  BACK = 'Back',
}

export const sendCarouselAnalyticsEvent = (
  title: string,
  action: CarouselAction,
  eventLocation: AnalyticsEventLocation | null,
) => {
  sendAnalyticsEvent({
    event: EventName.CAROUSEL,
    action: action,
    name: `${title}`,
    experiment_name: null,
    experiment_version: null,
    event_location: eventLocation ? eventLocation : null,
    method: null,
    list_id: null,
    list_name: null,
    type: null,
    status: null,
  });
};

interface CompareAnalyticsProps {
  product?: ProductSearchMaterial;
  eventName: string;
  projectData: {
    name: string;
    id: string;
  };
  withProductData?: boolean;
  additionalProps?: {
    [key: string]: any;
  };
}

export const pushCompareAnalyticsEvent = (data: CompareAnalyticsProps) => {
  try {
    const params = {
      event: data?.eventName,
      location: 'comparison_tool',
      event_location: 'Catalog',
      event_project_name: data?.projectData?.name,
      event_project_id: data?.projectData?.id,
      event_item_list_id: 'comparison_tool',
      event_item_list_name: 'Comparison tool',
    };
    let materialProps = {};
    if (data?.withProductData) {
      materialProps = {
        event_item_id: data?.product?.entityId,
        event_item_name: data?.product?.name,
        event_item_brand: data?.product?.manufacturer,
        event_item_type: getAnalyticsItemType(data?.product),
        event_item_variant: data?.product?.name,
        ...getAnalyticsItemCategoriesByTaxonomy(
          data?.product?.taxonomy || [],
          'event',
        ),
      };
    }
    const finalData = {
      ...params,
      ...materialProps,
      ...(data?.additionalProps || {}),
    };
    console.log(finalData);
    window.dataLayer.push(finalData);
  } catch (e) {
    console.log('Error pushCompareAnalyticsEvent');
    handleSystemErrorReport(
      `Error pushCompareAnalyticsEvent: ${e.message}`,
      'pushCompareAnalyticsEvent',
    );
  }
};

export const getEventsMaterialProps = (item: ProductSearchMaterial) => {
  const props: any = {};
  if (Array.isArray(item?.analyticsData?.impressions)) {
    props['impressions'] = item?.analyticsData?.impressions;
  }
  if (item?.analyticsData?.analyticsClass) {
    props['class'] = item?.analyticsData?.analyticsClass;
  }
  return props;
};
